import { useMemo } from 'react';
import TarjetasResumen, { type Indicador } from '../../../components/TarjetasResumen';
import { Seccion } from '../../../components/DetalleExpandible';
import { ESTADO_SKU, GONDOLA_DE_MARCA } from '../../../domain/yichang/catalogo';
import { claveTexto } from '../../../domain/pg/normalizar';
import { contar, pct, type Conteo, type ResumenBrechas } from '../../../domain/yichang/comun';
import {
  SIN_CONTESTAR, presenciaModerno, resumenCajas, resumenExhibiciones, resumenGondola,
} from '../../../domain/yichang/moderno';
import { etiquetaOpcion } from '../../../domain/yichang/respuestas';
import {
  resumenBodegas, resumenMercados, resumenPuestos, SIN_RESPUESTA,
} from '../../../domain/yichang/tradicional';
import {
  NOMBRE_MARCA,
  type CierreYiMercado, type FichaYiBodega, type FichaYiMercado, type FichaYiModerno, type InstrumentoYi,
  type MarcaCliente, type MarcaYi, type Presencia,
} from '../../../domain/yichang/tipos';
import { marcasDe } from './RegistrosYichang';
import { soles } from './Respuesta';

/** Una barra. Sin librería de gráficos: son `div`s de ancho porcentual. */
function Barra({ valor, color = 'bg-blue-600' }: { valor: number | null; color?: string }) {
  return (
    <span className="inline-block h-2 w-32 shrink-0 rounded bg-gray-100 align-middle">
      <span className={`block h-2 rounded ${color}`} style={{ width: `${Math.max(0, Math.min(100, valor ?? 0))}%` }} />
    </span>
  );
}

/**
 * Una lista de conteos con su denominador ESCRITO en el rótulo. `de` es el denominador de los
 * porcentajes; si es cero no se inventa un 0 %: se dice que no hubo a quién preguntar.
 */
function ListaConteo({ titulo, datos, de, nota }: {
  titulo: string; datos: readonly Conteo[]; de: number; nota?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs text-gray-500">{titulo}</p>
      {de === 0 || datos.length === 0 ? (
        <p className="text-sm text-gray-500">Nadie contestó esta pregunta en las fichas que se ven.</p>
      ) : (
        <ul className="space-y-1">
          {datos.map((d) => (
            <li key={d.valor} className="flex flex-wrap items-center gap-2 text-sm">
              {/* `basis` y no `w-`: con un ancho fijo el ítem de la grilla no puede encogerse por debajo de
                  él, y en tres columnas eso desplazaba la página de costado (la trampa del `min-width`). */}
              <span className="min-w-0 shrink grow-0 basis-64">{d.valor}</span>
              <Barra valor={pct(d.n, de)} />
              <b className="tabular-nums">{d.n}</b>
              <span className="text-xs text-gray-500">{pct(d.n, de)} %</span>
            </li>
          ))}
        </ul>
      )}
      {nota !== undefined && <p className="mt-1 text-xs text-gray-500">{nota}</p>}
    </div>
  );
}

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/** Los choques con su resultado, en palabras y con cuántos se pudieron calcular. */
function Brechas({ b, marca }: { b: ResumenBrechas; marca: string }) {
  if (b.calculados === 0) {
    return <p className="text-sm text-gray-600">Ningún choque tiene los dos precios con su gramaje: la brecha no se puede calcular todavía.</p>;
  }
  return (
    <p className="text-sm">
      Con la brecha calculada: <b>{plural(b.calculados, 'choque', 'choques')}</b>. {marca} está más caro en{' '}
      <b>{b.masCaro}</b>, más barato en <b>{b.masBarato}</b> y al mismo precio en <b>{b.igual}</b>.
      {b.mediana !== null && (
        <> La brecha mediana es <b>{b.mediana > 0 ? '+' : ''}{Math.round(b.mediana)} %</b> por 100 g.</>
      )}
      <span className="block text-xs text-gray-500">
        El precio por 100 g no se pregunta: lo calcula la pantalla con el gramaje. La mediana y no el promedio, para
        que un dígito de más no mueva la cifra.
      </span>
    </p>
  );
}

function Presencias({ p, de, marca, sujeto }: { p: Record<Presencia, number>; de: number; marca: string; sujeto: string }) {
  return (
    <p className="text-sm">
      {marca} se encontró en <b>{p.presente}</b> de {de} {sujeto}, no se encontró en <b>{p.ausente}</b> y en{' '}
      <b>{p.no_se_sabe}</b> no se puede saber.
      <span className="block text-xs text-gray-500">
        «No se puede saber» (no se pudo medir, no se contestó) no se cuenta como ausencia: sería un cero que nadie midió.
      </span>
    </p>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Canal moderno
// ═══════════════════════════════════════════════════════════════════════════

function GondolaResumen({ fichas, marca }: { fichas: readonly FichaYiModerno[]; marca: MarcaCliente }) {
  const r = useMemo(() => resumenGondola(fichas, marca), [fichas, marca]);
  const pres = useMemo(() => {
    const p: Record<Presencia, number> = { presente: 0, ausente: 0, no_se_sabe: 0 };
    for (const f of fichas) p[presenciaModerno(f, marca)] += 1;
    return p;
  }, [fichas, marca]);
  const g = r.g;
  const estados = [...ESTADO_SKU, SIN_CONTESTAR];
  return (
    <Seccion titulo={`Góndola ${g.letra} · ${g.nom.toLowerCase()} · ${g.marcaNom}`} ancho>
      <div className="space-y-5">
        <Presencias p={pres} de={r.fichas} marca={g.marcaNom} sujeto="tiendas" />
        <ListaConteo titulo={`¿Se pudo medir la góndola? · de ${r.fichas} tiendas`} datos={r.puerta.filter((c) => c.n > 0)} de={r.fichas} />

        {r.medidas === 0 ? (
          <p className="text-sm text-gray-600">Ninguna góndola de {g.nom.toLowerCase()} se pudo medir en estas fichas: no hay SKU, espacio ni precios que resumir.</p>
        ) : (
          <>
            <div>
              <p className="mb-1 text-xs text-gray-500">Estado de cada SKU núcleo · de {r.medidas} góndolas medidas</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">SKU</th>
                      {estados.map((e) => <th key={e} className="py-1 pr-3 text-right">{e}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {r.skus.map((s) => (
                      <tr key={s.k}>
                        <td className="py-1 pr-3">{s.nom}</td>
                        {estados.map((e) => {
                          const n = s.conteo.find((c) => c.valor === e)?.n ?? 0;
                          return <td key={e} className={`py-1 pr-3 text-right tabular-nums ${n === 0 ? 'text-gray-400' : ''}`}>{n}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                «Cenefa sin producto» es quiebre (lo arregla el mercaderista); «Ni cenefa ni producto» es, casi seguro,
                que la tienda no lo tiene listado (lo arregla el vendedor).
              </p>
            </div>

            <div>
              <p className="mb-1 text-xs text-gray-500">
                Espacio de cada marca · promedio por tienda, sobre {r.espacio.conEspacio} de {r.medidas} góndolas con el espacio pintado
              </p>
              {r.espacio.conEspacio === 0 ? <p className="text-sm text-gray-500">Ninguna góndola medida tiene el espacio pintado.</p> : (
                <ul className="space-y-1">
                  {r.espacio.marcas.map((m) => (
                    <li key={m.marca} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className={`w-48 shrink-0 ${m.marca === g.marcaNom ? 'font-semibold' : ''}`}>{m.marca}</span>
                      <Barra valor={m.pct} color={m.marca === g.marcaNom ? 'bg-blue-600' : 'bg-gray-400'} />
                      <b className="tabular-nums">{m.pct} %</b>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Sin las celdas «No cuenta» (lo que comparte el mueble y no es de la categoría). Se promedian los porcentajes
                de cada tienda: sumando celdas, la góndola más ancha valdría por tres.
              </p>
            </div>

            {r.frentes !== null && (
              <div>
                <p className="mb-1 text-xs text-gray-500">
                  Frentes de lata · suma de {r.frentes.conNumeros} tiendas que los contaron
                  {r.frentes.sinTramo > 0 && ` · ${plural(r.frentes.sinTramo, 'tienda', 'tiendas')} sin tramo de latas`}
                  {r.frentes.sinContestar > 0 && ` · ${r.frentes.sinContestar} sin contestar`}
                </p>
                {r.frentes.conNumeros === 0 ? <p className="text-sm text-gray-500">Ninguna tienda contó los frentes.</p> : (
                  <>
                    <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      {r.frentes.porMarca.map((x) => <li key={x.k}>{x.nom}: <b className="tabular-nums">{x.total}</b></li>)}
                    </ul>
                    <p className="mt-1 text-sm">
                      {r.frentes.pctCliente === null
                        ? 'Los frentes contados suman cero: no hay reparto que calcular.'
                        : <>Pringles tiene <b>{r.frentes.pctCliente} %</b> de los frentes contados.</>}
                    </p>
                  </>
                )}
              </div>
            )}

            {r.bloque !== null && (
              <ListaConteo titulo={`¿El bloque de Kellogg's está junto o partido? · de ${r.medidas} góndolas medidas`} datos={r.bloque} de={r.medidas} />
            )}

            <ListaConteo
              titulo={`¿Hay de ese producto en almacén? · según el personal · de ${plural(r.almacen.quiebres, 'quiebre', 'quiebres')} («Cenefa sin producto»)`}
              datos={r.almacen.conteo} de={r.almacen.quiebres}
              nota="Lo dice el personal de la tienda y no se puede comprobar: se reporta así, rotulado."
            />

            <div>
              <p className="mb-1 text-xs text-gray-500">Choque de precio obligatorio · {g.choque.propio} contra {g.choque.rival}</p>
              <p className="text-sm">
                Se pidió en <b>{r.choque.pedidos}</b> de {r.medidas} góndolas medidas: solo cuando {g.choque.propio} quedó «Está».
                En <b>{r.choque.noSePide}</b> no se pide porque quedó en otro estado («Cenefa sin producto», «Solo en
                exhibición», «No lo pude revisar»…)
                {r.choque.skuSinEstado > 0 && <>, y en <b>{r.choque.skuSinEstado}</b> el SKU quedó sin estado</>}.
                {r.choque.rivalNoEsta.length > 0 && (
                  <> El rival no estaba en {r.choque.rivalNoEsta.map((c) => `${c.n} («${c.valor}»)`).join(', ')}.</>
                )}
                {r.choque.sinContestar > 0 && <> Sin contestar: {r.choque.sinContestar}.</>}
                {r.choque.faltaPrecio > 0 && <> Falta un precio o su gramaje: {r.choque.faltaPrecio}.</>}
              </p>
              <Brechas b={r.choque.brechas} marca={g.marcaNom} />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <p className="text-sm">
                Material publicitario en la góndola: con material <b>{r.material.conMaterial}</b>, sin material{' '}
                <b>{r.material.sinMaterial}</b>{r.material.sinContestar > 0 && <>, sin contestar {r.material.sinContestar}</>} · de {r.medidas} góndolas medidas.
              </p>
              <p className="text-sm">
                Carteles de promoción: con carteles <b>{r.promocion.conCarteles}</b>, sin carteles{' '}
                <b>{r.promocion.sinCarteles}</b>{r.promocion.sinContestar > 0 && <>, sin contestar {r.promocion.sinContestar}</>} · de {r.medidas} góndolas medidas.
              </p>
              {r.material.piezasPorMarca.length > 0 && (
                <ListaConteo titulo={`Piezas de material, por marca · ${r.material.piezasPorMarca.reduce((s, c) => s + c.n, 0)} piezas`}
                  datos={r.material.piezasPorMarca} de={r.material.piezasPorMarca.reduce((s, c) => s + c.n, 0)} />
              )}
              {r.promocion.ofrece.length > 0 && (
                <ListaConteo titulo={`¿Qué ofrecen los carteles? · ${r.promocion.ofrece.reduce((s, c) => s + c.n, 0)} carteles`}
                  datos={r.promocion.ofrece} de={r.promocion.ofrece.reduce((s, c) => s + c.n, 0)} />
              )}
            </div>

            <ListaConteo titulo={`Impulsadora o degustación en el pasillo · de ${r.impulsadora.contestaron} góndolas que contestaron`}
              datos={r.impulsadora.conteo} de={r.impulsadora.contestaron}
              nota="Se puede marcar más de una (puede haber de la marca y de la competencia a la vez): la suma pasa del total." />
          </>
        )}

        <p className="text-sm">
          Publicidad de {g.marcaNom} en el camino al pasillo: en <b>{r.camino.conPublicidad}</b> de{' '}
          {plural(r.camino.contestaron, 'tienda que contestó', 'tiendas que contestaron')} (1.1).
        </p>
      </div>
    </Seccion>
  );
}

function ResumenModerno({ fichas, marca }: { fichas: readonly FichaYiModerno[]; marca: MarcaYi }) {
  const marcas = marcasDe(marca);
  const cajas = useMemo(() => resumenCajas(fichas), [fichas]);
  const enfocada = marca === 'las_dos' ? null : marca;
  const exh = useMemo(() => resumenExhibiciones(fichas, enfocada), [fichas, enfocada]);
  const indicadores: Indicador[] = [
    {
      titulo: 'Tiendas visitadas',
      valor: fichas.length,
      // Por la misma clave que el filtro «Tienda» de la segunda barra: «Wong  Benavides» y «wong benavides»
      // son una tienda, no dos.
      detalle: plural(new Set(fichas.map((f) => claveTexto(f.tiendaTexto) ?? '—')).size, 'tienda distinta', 'tiendas distintas'),
    },
    ...marcas.map((m): Indicador => {
      const r = resumenGondola(fichas, m);
      return { titulo: `Góndola de ${GONDOLA_DE_MARCA[m].nom.toLowerCase()} medida`, valor: r.medidas, detalle: `de ${fichas.length} tiendas`, tono: 'neutro' };
    }),
    ...marcas.map((m): Indicador => ({
      titulo: `${NOMBRE_MARCA[m]} se encontró`,
      valor: fichas.filter((f) => presenciaModerno(f, m) === 'presente').length,
      detalle: `de ${fichas.length} tiendas`,
      tono: 'bueno',
    })),
  ];
  return (
    <div className="space-y-6">
      <TarjetasResumen indicadores={indicadores} />
      {marcas.map((m) => <GondolaResumen key={m} fichas={fichas} marca={m} />)}
      {marcas.includes('pringles') && (
        <Seccion titulo="Zona de cajas · Pringles" ancho>
          {cajas.contaron === 0 ? <p className="text-sm text-gray-600">Ninguna tienda contó las cajas.</p> : (
            <div className="space-y-2 text-sm">
              <p>
                Cajas con Pringles: <b>{cajas.conPringles}</b> de <b>{cajas.abiertas}</b> cajas abiertas
                {pct(cajas.conPringles, cajas.abiertas) !== null && <> ({pct(cajas.conPringles, cajas.abiertas)} %)</>},
                en {plural(cajas.contaron, 'tienda que contó', 'tiendas que contaron')} las dos cosas.
              </p>
              <p>
                Cajas con otra papa: <b>{cajas.conOtraPapa}</b> de <b>{cajas.abiertasOtraPapa}</b> cajas abiertas, en{' '}
                {plural(cajas.contaronOtraPapa, 'tienda que', 'tiendas que')} también contestaron 4.10.
              </p>
              {cajas.formatos.length > 0 && (
                <ListaConteo titulo={`¿En qué formato está Pringles en la caja? (4.9) · de ${plural(cajas.contaronFormato, 'tienda que contestó', 'tiendas que contestaron')}`}
                  datos={cajas.formatos} de={cajas.contaronFormato}
                  nota="Se puede marcar más de uno: la suma pasa del total." />
              )}
            </div>
          )}
        </Seccion>
      )}
      <Seccion titulo={enfocada === null ? 'Exhibiciones fuera del pasillo'
        : `Exhibiciones fuera del pasillo · lado de ${NOMBRE_MARCA[enfocada]} (la marca y su competencia)`} ancho>
        <div className="grid gap-4 md:grid-cols-3">
          <ListaConteo titulo={`Por marca · ${exh.total} exhibiciones`} datos={exh.porMarca} de={exh.total} />
          <ListaConteo titulo={`¿Cuánto producto tenía? · de ${plural(exh.total, 'exhibición', 'exhibiciones')}`} datos={exh.porLleno} de={exh.total} />
          <ListaConteo titulo={`¿Cómo terminó la vuelta? · de ${fichas.length} tiendas`} datos={exh.termino} de={fichas.length}
            nota="Separa «no había» de «no se miró»: cero exhibiciones en una vuelta incompleta no es cero." />
        </div>
      </Seccion>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bodegas
// ═══════════════════════════════════════════════════════════════════════════

function BodegaResumen({ fichas, marca }: { fichas: readonly FichaYiBodega[]; marca: MarcaCliente }) {
  const r = useMemo(() => resumenBodegas(fichas, marca), [fichas, marca]);
  const nombre = NOMBRE_MARCA[marca];
  const columnas = [...new Set(r.t4b.flatMap((x) => x.conteo.filter((c) => c.n > 0).map((c) => c.valor)))];
  return (
    <Seccion titulo={`${nombre} · ${marca === 'pringles' ? 'snacks salados' : 'cereal'}`} ancho>
      <div className="space-y-5">
        <Presencias p={r.presencia} de={r.n} marca={nombre} sujeto="bodegas" />
        <ListaConteo titulo={`¿Vende ${marca === 'pringles' ? 'snacks salados' : 'cereal'}? (T0) · de ${r.n} bodegas`} datos={r.vende} de={r.n} />

        <div>
          <p className="mb-1 text-xs text-gray-500">
            Lo que no se ve, preguntado como comprador (T4b) · de {r.n} bodegas. «Se le acabó» es quiebre: la trabaja y hoy
            no tiene. «No la trabaja» es falta de cobertura. Son dos conversaciones distintas.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="py-1 pr-3">Producto</th>
                  {columnas.map((c) => <th key={c} className="py-1 pr-3 text-right">{c}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {r.t4b.map((x) => (
                  <tr key={x.k}>
                    <td className="py-1 pr-3"><span className="mr-1 font-mono text-[11px] text-gray-500">{x.cod}</span>{x.nom}</td>
                    {columnas.map((c) => {
                      const n = x.conteo.find((y) => y.valor === c)?.n ?? 0;
                      return <td key={c} className={`py-1 pr-3 text-right tabular-nums ${n === 0 ? 'text-gray-400' : ''}`}>{n}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <ListaConteo titulo={`¿Por qué no trabaja ${nombre}? (T4c) · declarado · de ${r.t4c.contestaron} bodegas que contestaron`}
          datos={r.t4c.motivos} de={r.t4c.contestaron}
          nota="Solo se pregunta identificado o mixto, y a quien dijo que no la trabaja. Se puede marcar más de uno." />

        <div className="grid gap-4 md:grid-cols-3">
          <ListaConteo titulo={`¿Dónde compra ${nombre}? (T7.1) · declarado · de ${r.t7.contestaron} bodegas`}
            datos={r.t7.donde} de={r.t7.contestaron} nota="Se puede marcar más de uno: la suma pasa del total." />
          <ListaConteo titulo={`¿Cada cuánto compra? (T7.2) · declarado · de ${plural(r.t7.frecuencia.reduce((s, c) => s + c.n, 0), 'que contestó', 'que contestaron')}`}
            datos={r.t7.frecuencia} de={r.t7.frecuencia.reduce((s, c) => s + c.n, 0)} />
          <ListaConteo titulo={`¿Cómo lo paga? (T7.3) · declarado · de ${plural(r.t7.pago.reduce((s, c) => s + c.n, 0), 'que contestó', 'que contestaron')}`}
            datos={r.t7.pago} de={r.t7.pago.reduce((s, c) => s + c.n, 0)} />
        </div>

        <div>
          <p className="mb-1 text-xs text-gray-500">
            Choque de precio obligatorio (T5.6) · {marca === 'pringles' ? "Pringles chica contra Lay's 36 g o Piqueo Snax 55 g" : "bolsa de Kellogg's contra bolsa de Ángel"}
          </p>
          <p className="text-sm">
            Aplica en <b>{r.choque.aplica}</b> de {r.n} bodegas (las que venden la categoría).
            {r.choque.sinPar > 0 && <> Sin par elegido: {r.choque.sinPar}.</>}
            {r.choque.faltaLado.length > 0 && <> Con un lado que faltó: {r.choque.faltaLado.map((c) => `${c.valor} (${c.n})`).join(', ')}.</>}
            {r.choque.faltaPrecio > 0 && <> Falta un precio o su gramaje: {r.choque.faltaPrecio}.</>}
          </p>
          {r.choque.pares.length > 0 && (
            <p className="text-sm text-gray-700">Pares anotados: {r.choque.pares.map((c) => `${c.valor} (${c.n})`).join(' · ')}.</p>
          )}
          <Brechas b={r.choque.brechas} marca={nombre} />
        </div>

        {r.dondePringles !== null && (
          <ListaConteo titulo={`¿Dónde está Pringles? (T3.7) · de ${r.dondePringles.reduce((s, c) => s + c.n, 0)} bodegas que contestaron`}
            datos={r.dondePringles} de={r.dondePringles.reduce((s, c) => s + c.n, 0)} />
        )}

        <p className="text-sm">
          Bodegas con algún mueble de {nombre} (T3.2): <b>{r.conMueble}</b> de {r.n}. Con material de {nombre} afuera
          (T2.2 a T2.8): <b>{r.conFachada}</b> de {r.n}.
        </p>

        <ListaConteo titulo={`¿Qué ${marca === 'pringles' ? 'papitas' : 'cereal'} recomienda el bodeguero? (T6) · referencial · de ${plural(r.recomienda.contestaron, 'bodega que respondió', 'bodegas que respondieron')}`}
          datos={r.recomienda.marcas} de={r.recomienda.contestaron}
          nota={r.recomienda.noRespondio > 0
            ? `Aparte, en ${plural(r.recomienda.noRespondio, 'bodega', 'bodegas')} no contestó o no se pudo preguntar: no entra en el porcentaje.`
            : undefined} />
      </div>
    </Seccion>
  );
}

function ResumenBodegas({ fichas, marca }: { fichas: readonly FichaYiBodega[]; marca: MarcaYi }) {
  const marcas = marcasDe(marca);
  const tipos = useMemo(() => contar(fichas.map((f) => (f.tipoTienda === null ? SIN_RESPUESTA : etiquetaOpcion(f.tipoTienda)))), [fichas]);
  const modos = useMemo(() => contar(fichas.map((f) => (f.modoVisita === null ? SIN_RESPUESTA : etiquetaOpcion(f.modoVisita)))), [fichas]);
  const indicadores: Indicador[] = [
    { titulo: 'Bodegas visitadas', valor: fichas.length, detalle: plural(new Set(fichas.map((f) => claveTexto(f.zona) ?? '—')).size, 'zona', 'zonas') },
    ...marcas.map((m): Indicador => {
      const r = resumenBodegas(fichas, m);
      return { titulo: `${NOMBRE_MARCA[m]} se encontró`, valor: r.presencia.presente, detalle: `de ${fichas.length} bodegas`, tono: 'bueno' };
    }),
  ];
  return (
    <div className="space-y-6">
      <TarjetasResumen indicadores={indicadores} />
      <Seccion titulo="Las bodegas" ancho>
        <div className="grid gap-4 md:grid-cols-2">
          <ListaConteo titulo={`Tipo de bodega (E.4) · de ${fichas.length}`} datos={tipos} de={fichas.length} />
          <ListaConteo titulo={`¿Cómo entró el supervisor? (E.5) · de ${fichas.length}`} datos={modos} de={fichas.length}
            nota="Como cliente no se pregunta todo: T4c no aparece y las fotos son opcionales." />
        </div>
      </Seccion>
      {marcas.map((m) => <BodegaResumen key={m} fichas={fichas} marca={m} />)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Mercados
// ═══════════════════════════════════════════════════════════════════════════

function MercadoResumen({ fichas, cierres, marca }: {
  fichas: readonly FichaYiMercado[]; cierres: readonly CierreYiMercado[]; marca: MarcaCliente;
}) {
  const r = useMemo(() => resumenMercados(fichas, cierres, marca), [fichas, cierres, marca]);
  const nombre = NOMBRE_MARCA[marca];
  const cat = marca === 'pringles' ? 'snacks salados' : 'cereales';
  const columnas = [...new Set(r.estados.flatMap((x) => x.conteo.filter((c) => c.n > 0).map((c) => c.valor)))];
  return (
    <Seccion titulo={`${nombre} · ${cat}`} ancho>
      <div className="space-y-5">
        <Presencias p={r.presencia} de={r.visitados} marca={nombre} sujeto="puestos visitados" />
        <p className="text-sm">
          Venden {cat}: <b>{r.vendenCategoria}</b> de {r.levantados} puestos levantados (m2.2). Ese es el denominador de lo que sigue.
        </p>
        {r.vendenCategoria === 0 ? (
          <p className="text-sm text-gray-600">Ningún puesto levantado vende {cat} en estas fichas.</p>
        ) : (
          <>
            <div>
              <p className="mb-1 text-xs text-gray-500">Marca por marca, ¿cómo está hoy? (m4.1) · de {r.vendenCategoria} puestos que venden {cat}</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">Marca</th>
                      {columnas.map((c) => <th key={c} className="py-1 pr-3 text-right">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {r.estados.map((x) => (
                      <tr key={x.marca}>
                        <td className={`py-1 pr-3 ${x.esCliente ? 'font-semibold' : ''}`}>{x.marca}</td>
                        {columnas.map((c) => {
                          const n = x.conteo.find((y) => y.valor === c)?.n ?? 0;
                          return <td key={c} className={`py-1 pr-3 text-right tabular-nums ${n === 0 ? 'text-gray-400' : ''}`}>{n}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-gray-500">«Se le acabó» es venta perdida esta semana; «No la trabaja», una marca que nunca entró al puesto.</p>
            </div>

            {r.tamanos !== null && (
              <ListaConteo titulo={`Pringles: ¿en qué tamaño la tiene? (m4.2) · de ${r.tamanos.de} puestos con Pringles`}
                datos={r.tamanos.conteo} de={r.tamanos.de} nota="Se puede marcar más de uno." />
            )}
            {r.granel !== null && (
              <ListaConteo titulo={`¿Vende cereal a granel, por kilo? (m4.3) · de ${r.granel.reduce((s, c) => s + c.n, 0)} que contestaron`}
                datos={r.granel} de={r.granel.reduce((s, c) => s + c.n, 0)} />
            )}

            <div>
              <p className="mb-1 text-xs text-gray-500">Precios de {cat} (m5) · {r.precios.reduce((s, x) => s + x.n, 0)} precios levantados</p>
              {r.precios.length === 0 ? <p className="text-sm text-gray-500">No se levantó ningún precio de {cat}.</p> : (
                <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">Marca</th>
                      <th className="py-1 pr-3 text-right">Precios</th>
                      <th className="py-1 pr-3 text-right">Mediana por 100 g</th>
                      <th className="py-1 text-right">Sobre</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {r.precios.map((x) => (
                      <tr key={x.marca}>
                        <td className={`py-1 pr-3 ${x.esCliente ? 'font-semibold' : ''}`}>{x.marca}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{x.n}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{soles(x.mediana100)}</td>
                        <td className="py-1 text-right text-xs text-gray-500">{x.con100} de {x.n} con gramaje</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">El precio por 100 g lo calcula la pantalla; a granel el precio es por kilo y queda fuera de la mediana.</p>
            </div>

            <ListaConteo titulo={`Si el cliente no pide marca, ¿cuál le ofrece primero? (m8.1) · referencial · de ${plural(r.ofrece.contestaron, 'puesto que respondió', 'puestos que respondieron')}`}
              datos={r.ofrece.conteo} de={r.ofrece.contestaron}
              nota={r.ofrece.noRespondio > 0
                ? `Aparte, en ${plural(r.ofrece.noRespondio, 'puesto', 'puestos')} no contestó o no se pudo preguntar: no entra en el porcentaje.`
                : undefined} />
          </>
        )}
      </div>
    </Seccion>
  );
}

function ResumenMercados({ fichas, cierres, marca }: {
  fichas: readonly FichaYiMercado[]; cierres: readonly CierreYiMercado[]; marca: MarcaYi;
}) {
  const marcas = marcasDe(marca);
  // Lo que no depende de la marca (visitados, motivos, abasto, jornadas) se calcula aparte, sin elegir una.
  const base = useMemo(() => resumenPuestos(fichas, cierres), [fichas, cierres]);
  const indicadores: Indicador[] = [
    { titulo: 'Puestos visitados', valor: base.visitados, detalle: 'la base de todo lo de abajo' },
    { titulo: 'Levantados', valor: base.levantados, detalle: `de ${base.visitados} visitados`, tono: 'bueno' },
    { titulo: 'No se pudo', valor: base.noSePudo, detalle: `de ${base.visitados} visitados`, tono: 'atencion' },
    { titulo: 'Jornadas cerradas', valor: base.jornadas.cerradas, detalle: `de ${base.jornadas.mercadoDias} mercado-día con fichas`,
      tono: base.jornadas.cerradas < base.jornadas.mercadoDias ? 'atencion' : 'bueno' },
  ];
  return (
    <div className="space-y-6">
      <TarjetasResumen indicadores={indicadores} />
      {base.noSePudo > 0 && (
        <Seccion titulo="Los puestos que no se pudieron levantar" ancho>
          <ListaConteo titulo={`¿Por qué no? (m1.4) · de ${base.noSePudo} puestos`} datos={base.motivos} de={base.noSePudo}
            nota="Un puesto cerrado también es un dato: sin él, un recorrido de treinta puestos con ocho medidos se lee como uno de ocho." />
        </Seccion>
      )}
      {marcas.map((m) => <MercadoResumen key={m} fichas={fichas} cierres={cierres} marca={m} />)}
      <Seccion titulo="De dónde saca el producto (m7) · es del puesto, no de una marca" ancho>
        <div className="grid gap-4 md:grid-cols-2">
          <ListaConteo titulo={`¿De dónde saca el producto? · declarado · de ${base.abasto.contestaron} puestos que contestaron`}
            datos={base.abasto.donde} de={base.abasto.contestaron} nota="Se puede marcar más de uno: la suma pasa del total." />
          <ListaConteo titulo={`¿Cada cuánto compra? · declarado · de ${plural(base.abasto.frecuencia.reduce((s, c) => s + c.n, 0), 'puesto que contestó', 'puestos que contestaron')}`}
            datos={base.abasto.frecuencia} de={base.abasto.frecuencia.reduce((s, c) => s + c.n, 0)} />
        </div>
      </Seccion>
    </div>
  );
}

/**
 * El consolidado de Yichang, calculado sobre **exactamente las filas que muestra «Registros»** —con el
 * filtro de la segunda barra y el de «solo donde se encontró»—, no con una segunda consulta.
 *
 * 🔴 Cada cifra lleva su denominador escrito al lado, y no son todos el mismo: los SKU van sobre las
 * góndolas medidas, el almacén sobre los quiebres, m4 sobre los puestos que venden la categoría. Con
 * cero fichas se dice con palabras: una tabla vacía o un 0 % se leerían como un resultado.
 */
export default function ResumenYichang({
  instrumento, moderno, bodegas, mercados, cierres, marca, estudioVacio, hayFiltro, cargando,
}: {
  instrumento: InstrumentoYi;
  moderno: readonly FichaYiModerno[];
  bodegas: readonly FichaYiBodega[];
  mercados: readonly FichaYiMercado[];
  cierres: readonly CierreYiMercado[];
  marca: MarcaYi;
  estudioVacio: boolean;
  /** Con filtro, el cero lo puso el filtro y no el rango: se dice cuál de los dos. */
  hayFiltro: boolean;
  cargando: boolean;
}) {
  const n = instrumento === 'moderno' ? moderno.length : instrumento === 'bodegas' ? bodegas.length : mercados.length;
  if (n === 0 && cargando) return <p className="text-sm text-gray-600">Cargando las fichas…</p>;
  if (n === 0) {
    return (
      <p className="text-sm text-gray-600">
        {estudioVacio
          ? 'Todavía no llegó ninguna ficha de Yichang de este instrumento, así que no hay nada que resumir. El resumen se arma solo cuando llegue la primera.'
          : hayFiltro
            ? 'El filtro dejó todas las fichas afuera, así que no hay nada que resumir. Límpialo desde la línea de arriba.'
            : 'No hay fichas en este rango para resumir.'}
      </p>
    );
  }
  if (instrumento === 'moderno') return <ResumenModerno fichas={moderno} marca={marca} />;
  if (instrumento === 'bodegas') return <ResumenBodegas fichas={bodegas} marca={marca} />;
  return <ResumenMercados fichas={mercados} cierres={cierres} marca={marca} />;
}
